import { useState, useEffect } from "react";
import { useParams, useRouter, createFileRoute } from "@tanstack/react-router";
import {
  useClass,
  useClassStudents,
  useRemoveStudentFromClass,
  useGenerateInviteCode,
} from "@/lib/api/classes/hooks";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, Trash2, Copy, RefreshCw } from "lucide-react";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { toast } from "sonner";
import { AddStudentDialog } from "@/components/classes/add-student-dialog";
import { useGetProfile } from "@/lib/api/auth/hooks";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";

function ClassStudentsPage() {
  const params = useParams({ strict: false });
  const router = useRouter();
  const classId = params.id as string;

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteCodeExpiresAt, setInviteCodeExpiresAt] = useState<Date | null>(null);
  const { data: user, isLoading: isUserLoading } = useGetProfile();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const { data: classData, isLoading: isClassLoading } = useClass(classId);
  const { data: students, isLoading: isStudentsLoading } =
    useClassStudents(classId);
  const removeStudent = useRemoveStudentFromClass();
  const generateInviteCode = useGenerateInviteCode();

  // 기존 초대 코드가 있으면 state에 설정
  useEffect(() => {
    if (classData?.inviteCode && classData?.inviteCodeExpiresAt) {
      const expiresAt = new Date(classData.inviteCodeExpiresAt);
      // 만료되지 않은 경우에만 설정
      if (expiresAt > new Date()) {
        setInviteCode(classData.inviteCode);
        setInviteCodeExpiresAt(expiresAt);
      }
    }
  }, [classData]);

  const canModify = () => {
    if (!user || !classData) return false;
    if (user.role === "admin") return true;
    return classData.teacher._id === user._id;
  };

  const handleRemoveStudent = (studentId: string, studentName: string) => {
    setAlertDialogState({
      title: "학생 제거",
      description: `정말 "${studentName}" 학생을 반에서 제거하시겠습니까?`,
      confirmText: "제거",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await removeStudent.mutateAsync({ classId, studentId });
          toast.success("학생을 제거했습니다.");
        } catch {
          toast.error("학생 제거에 실패했습니다.");
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  const handleGenerateInviteCode = async () => {
    try {
      const response = await generateInviteCode.mutateAsync(classId);
      setInviteCode(response.data.inviteCode);
      setInviteCodeExpiresAt(new Date(response.data.expiresAt));
      toast.success("초대 코드가 생성되었습니다.");
    } catch {
      toast.error("초대 코드 생성에 실패했습니다.");
    }
  };

  const handleCopyInviteCode = () => {
    if (inviteCode) {
      navigator.clipboard.writeText(inviteCode);
      toast.success("초대 코드가 복사되었습니다.");
    }
  };

  const isInviteCodeExpired = inviteCodeExpiresAt && new Date() > inviteCodeExpiresAt;

  useLoadingIndicator(isUserLoading || isClassLoading || isStudentsLoading);

  return (
    <PageContainer>
      <Header
        title={classData ? `${classData.name} - 학생 관리` : "학생 관리"}
        left={
          <Button variant="ghost" size="icon" onClick={() => router.history.back()}>
            <ArrowLeft className="size-4" />
          </Button>
        }
        right={
          canModify() && (
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="size-4 mr-2" />
              학생 추가
            </Button>
          )
        }
      />

      {/* 반 정보 */}
      {classData && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg">
          <div className="text-sm text-muted-foreground">
            담당: {classData.teacher?.name || "-"}
            {classData.office?.name && ` · ${classData.office.name}`}
          </div>
          {classData.description && (
            <div className="text-sm text-muted-foreground mt-1">
              {classData.description}
            </div>
          )}
        </div>
      )}

      {/* 초대 코드 */}
      {canModify() && (
        <div className="mb-6 p-4 bg-card rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium">학생 초대 코드</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateInviteCode}
              disabled={generateInviteCode.isPending}
            >
              <RefreshCw className={`size-4 mr-2 ${generateInviteCode.isPending ? 'animate-spin' : ''}`} />
              {inviteCode ? '재생성' : '코드 생성'}
            </Button>
          </div>
          {inviteCode ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className={`text-2xl font-mono tracking-widest ${isInviteCodeExpired ? 'text-muted-foreground line-through' : ''}`}>
                  {inviteCode}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopyInviteCode}
                  disabled={!!isInviteCodeExpired}
                >
                  <Copy className="size-4" />
                </Button>
              </div>
              <div className={`text-sm ${isInviteCodeExpired ? 'text-destructive' : 'text-muted-foreground'}`}>
                {isInviteCodeExpired
                  ? '만료됨 - 새 코드를 생성해주세요'
                  : `만료: ${inviteCodeExpiresAt?.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} (1시간)`}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              코드를 생성하면 학생이 앱에서 직접 반에 등록할 수 있습니다.
            </p>
          )}
        </div>
      )}

      {/* 학생 목록 */}
      <div className="bg-card rounded-lg border">
        {students && students.length > 0 ? (
          <>
            {/* 테이블 헤더 */}
            <div className="hidden md:grid grid-cols-5 gap-4 p-4 border-b font-semibold text-sm text-muted-foreground">
              <div>이름</div>
              <div>아이디</div>
              <div>학교</div>
              <div>전화번호</div>
              <div></div>
            </div>

            {/* 테이블 바디 */}
            <div className="divide-y">
              {students.map((student) => (
                <div
                  key={student._id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                >
                  {/* 데스크톱 뷰 */}
                  <div className="hidden md:grid grid-cols-5 gap-4 items-center">
                    <div className="font-medium">{student.name}</div>
                    <div className="text-sm">{student.userId}</div>
                    <div className="text-sm text-muted-foreground">
                      {student.school || "-"}
                    </div>
                    <div className="text-sm">{student.phoneNumber || "-"}</div>
                    <div className="flex gap-1 justify-end">
                      {canModify() && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleRemoveStudent(student._id, student.name)
                          }
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* 모바일 뷰 */}
                  <div className="md:hidden space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{student.name}</span>
                      {canModify() && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleRemoveStudent(student._id, student.name)
                          }
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {student.userId}
                      {student.school && ` · ${student.school}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            등록된 학생이 없습니다.
          </div>
        )}
      </div>

      {/* 학생 추가 다이얼로그 */}
      {classData?.office?._id && (
        <AddStudentDialog
          classId={classId}
          officeId={classData.office._id}
          currentStudents={students || []}
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
        />
      )}
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/classes/$id/students")({
  component: ClassStudentsPage,
});
